import { forwardRef, useMemo } from 'react'
import { Link, NavLink, matchPath, useLocation, useParams } from 'react-router-dom'
import { format } from 'date-fns'
import { resolveReturnPath } from '@/common/entities/navigationHistory'
import { Button } from '@/common/ui/Form/Button'
import { Icon, type IconName } from '@/common/ui/Icon'
import { Tab, TabItem, useTabSlidingUnderline } from '@/common/ui/Tab'
import { useProduct } from '../model'
import { InsufficientBalanceModal } from '../../insufficientBalance/ui'

type ProductTab = {
    to: 'analytics' | 'texts' | 'media'
    label: string
    icon: IconName
}

const PRODUCT_TABS: ProductTab[] = [
    { to: 'analytics', label: 'Данные и анализ', icon: 'chart' },
    { to: 'texts', label: 'Тексты и SEO', icon: 'text' },
    { to: 'media', label: 'Фото и видео', icon: 'gallery' },
]

const ProductTabLink = forwardRef<HTMLDivElement, ProductTab>(function ProductTabLink({ to, label, icon }, ref) {
    return (
        <TabItem ref={ref}>
            <NavLink
                to={to}
                role='tab'
                aria-label={label}
                tabIndex={0}
                className={({ isActive }) => `tab-link${isActive ? ' active' : ''}`}
                end
            >
                <Icon name={icon} className='w-5 h-5' />
                <span className='text-base font-normal whitespace-nowrap'>{label}</span>
            </NavLink>
        </TabItem>
    )
})

const ProductTabs = () => {
    const { pathname } = useLocation()
    const { productId } = useParams()

    const activeIndex = useMemo(() => {
        const idx = PRODUCT_TABS.findIndex(tab =>
            productId ? matchPath({ path: `/app/products/:productId/${tab.to}`, end: true }, pathname) != null : false
        )
        return idx >= 0 ? idx : 0
    }, [pathname, productId])

    const { underline, setItemRef } = useTabSlidingUnderline(activeIndex)

    return (
        <Tab
            className='tab2--sliding-indicator'
            underlineIndicator={
                <div
                    className='tab2-sliding-underline'
                    style={{ transform: `translateX(${underline.x}px)`, width: underline.width }}
                    aria-hidden
                />
            }
        >
            {PRODUCT_TABS.map((tab, i) => (
                <ProductTabLink key={tab.to} ref={setItemRef(i)} {...tab} />
            ))}
        </Tab>
    )
}

const marketPlaceIconName: Record<string, IconName> = {
    wildberries: 'wb',
    ozon: 'ozon',
}

export const ProductHeader = () => {
    const { product, isLoading, error } = useProduct()

    if (isLoading) {
        return <div className='flex justify-center items-center h-64'>Загрузка...</div>
    }

    if (error) {
        return <div className='flex justify-center items-center h-64 text-red-500'>Ошибка загрузки данных</div>
    }

    if (!product) {
        return null
    }

    return (
        <div className='profile-content text-white w-full'>
            <InsufficientBalanceModal />
            <div className='relative w-full'>
                <div className='flex items-center justify-between w-full'>
                    <div className='flex gap-2 items-center'>
                        <Button
                            as={Link}
                            to={resolveReturnPath('/app/products')}
                            className='flex gap-2 items-center'
                            title='Назад к списку'
                            variant='ghost'
                            size='sm'
                            aria-label='Назад к списку'
                        >
                            <Icon name='arrow-left' className='w-5 h-5' />
                        </Button>
                        {product.marketplace && (
                            <Icon
                                name={marketPlaceIconName[product.marketplace]}
                                className='min-w-[28px] min-h-[28px]'
                            />
                        )}
                        <h1 className='font-medium text-2xl text-white leading-[22px]'>{product.name}</h1>
                    </div>
                </div>
                <div className='text-[#aba7af] text-sm mb-5 ml-7 mt-3'>
                    Обновление импорта от:{' '}
                    {product.updated_at ? format(new Date(product.updated_at), 'dd.MM.yyyy HH:mm') : '—'}{' '}
                    {/*<span className='text-[#00B998] text-sm'>[public]</span>*/}
                </div>
            </div>
            <ProductTabs />
        </div>
    )
}
